<template>
  <div class="inviteBulkLoad">
    <CwePageHeader :authenticationRequired="true" />
    <div id="MainInviteBulkLoad">
      <h5 class="sectionHeader">Invite Bulk Load</h5>
      <p class="intro">
        Upload a CSV of invites, review and correct the rows below, then import. Saved rows stay here marked Created or
        Updated; rows that fail to save show the reason so you can fix and import them again.
      </p>

      <p v-if="pageError" class="alertBox">{{ pageError }}</p>

      <div class="pagePanel">
        <InviteCsvUpload :key="uploadKey" @file-selected="onFileSelected" @parsed="onParsed" @batch-error="onBatchError" />
      </div>

      <div class="pagePanel batchParams">
        <h6 class="subHeader">Applied to every invite</h6>
        <p class="batchHint">
          These values are copied into each invite row below. Every invite is created for the selected Source App.
        </p>

        <div class="paramGrid">
          <div class="section">
            <div class="label-bold"><label for="sourceApp">Source App</label></div>
            <vue-multiselect id="sourceApp"
                             v-model="selectedApplication"
                             :options="applications"
                             track-by="key"
                             label="name"
                             :show-labels="false"
                             placeholder="Select a source app" />
          </div>
          <div class="section">
            <div class="label-bold"><label for="country">Country</label></div>
            <vue-multiselect id="country"
                             v-model="selectedCountry"
                             :options="countries"
                             track-by="abbreviation"
                             label="name"
                             :show-labels="false"
                             placeholder="Select a country" />
          </div>
          <div class="section">
            <div class="label-bold"><label for="language">Language</label></div>
            <vue-multiselect id="language"
                             v-model="selectedLanguage"
                             :options="languages"
                             track-by="code"
                             label="label"
                             :show-labels="false"
                             placeholder="Select a language" />
          </div>
          <div class="section">
            <div class="label-bold"><label for="accountType">Application account type</label></div>
            <vue-multiselect id="accountType"
                             v-model="selectedAccountType"
                             :options="accountTypes"
                             track-by="name"
                             label="name"
                             :show-labels="false"
                             placeholder="Select an application account type" />
          </div>
          <div class="section">
            <div class="label-bold"><label for="userAccountType">User account type</label></div>
            <vue-multiselect id="userAccountType"
                             v-model="selectedUserAccountType"
                             :options="userAccountTypes"
                             track-by="name"
                             label="name"
                             :show-labels="false"
                             placeholder="Select a user account type" />
          </div>
          <div class="section">
            <div class="label-bold"><label for="name">Account name</label></div>
            <input id="name" v-model="name" type="text" />
          </div>
          <div class="section">
            <div class="label-bold"><label for="externalId">External ID</label></div>
            <input id="externalId" v-model="externalId" type="text" />
          </div>
          <div class="section">
            <div class="label-bold"><label for="authPathSegment">Auth path segment</label></div>
            <input id="authPathSegment" v-model="authPathSegment" type="text" />
          </div>
        </div>
      </div>

      <div class="pagePanel invitesPanel">
        <div class="invitesHeader">
          <h5 class="invitesHeading">Invites</h5>
          <div class="invitesActions">
            <button v-if="rows.length > 0"
                    type="button"
                    class="exportBtn"
                    title="Export CSV"
                    aria-label="Export CSV"
                    @click="onExport">
              <font-awesome-icon icon="fa-solid fa-file-export" />
            </button>
            <button type="button" class="addInviteBtn" @click="onAddRow">
              <font-awesome-icon icon="fa-solid fa-plus" />&nbsp;Invite
            </button>
          </div>
        </div>

        <div v-if="submitSummary" class="infoBox" role="status">
          <font-awesome-icon icon="fa-solid fa-circle-info" class="infoIcon" />
          <span>
            Processed {{ submitSummary.processed }}: {{ submitSummary.created }} created,
            {{ submitSummary.updated }} updated, {{ submitSummary.failed }} failed.
            <template v-if="submitSummary.failed > 0">
              Rows that failed show the reason in red in the Status column — fix them and import again.
            </template>
            <template v-else>All invites saved successfully.</template>
          </span>
        </div>

        <InviteBulkGrid :rows="rows"
                        :cellErrors="cellErrors"
                        :rowErrors="submitRowErrors"
                        :rowStatuses="rowStatuses"
                        :applications="applications"
                        @cell-change="onCellChange"
                        @delete-row="onDeleteRow" />

        <div id="submitSection" class="submitSection">
          <div class="submitActions">
            <button type="button"
                    class="resetBtn"
                    :disabled="submitting"
                    @click="onReset">
              Reset
            </button>
            <p v-if="submitBlockedReason" class="error submitHint">{{ submitBlockedReason }}</p>
            <button type="button"
                    class="importBtn"
                    :disabled="!canSubmit"
                    @click="onSubmit">
              Import
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import * as Vue from 'vue';
import CwePageHeader from '../components/CwePageHeader.vue';
import InviteCsvUpload from '../components/InviteCsvUpload.vue';
import InviteBulkGrid from '../components/InviteBulkGrid.vue';
import {
  InviteAccountTypeOption,
  InviteApplicationOption,
  InviteRow,
  InviteRowCellError,
  InviteRowStatus,
  LoadInvitesRequest,
  LoadInvitesResult,
  ParseInvitesResult
} from '@/types/adminInvites';
import { ICountry } from '@/models/Country';
import { DefaultLanguageCode, SupportedLanguageOption, SupportedLanguages } from '@/constants/supportedLanguages';
import { InviteColumnByHeader, createBlankInviteRow } from '@/constants/inviteColumns';
import { validateInviteRows } from '../services/inviteRowValidation';
import { normalizeInvitePhone } from '../services/invitePhone';
import { downloadBlob } from '../services/fileDownload';
import { serializeInvitesCsv } from '../services/csvSerialize';
import { getCountries } from '../api/endpoints/countryWeb';
import {
  AdminInvitesBatchError,
  exportInvitesCsv,
  getInviteAccountTypes,
  getInviteApplications,
  getInviteUserAccountTypes,
  loadInvites
} from '../api/endpoints/adminInvitesWeb';

// MM-42243: orchestrator for the bulk invite-load page. It is the single owner of grid state — the canonical row
// array, the reference-data selections shared by every row, and the parse-time cell errors — wiring the upload to the
// grid. A nested "Applied to every invite" subsection drives the batch: Source App (a single app), Country, Language
// and User Account Type are copied into each row's cells (which stay individually editable), and the Source App is
// also the application the batch grants — one invite per row. The Application Account Type is submitted only as
// AccountTypeName (the tenant type the app must be configured for) and is a different DataType vocabulary from the
// per-row User Account Type (ClinicalUser/ZBEmployee). The submit path serializes the unsaved rows to CSV, posts to
// :load, then marks each row Created/Updated (read-only) or annotates it with the API's failure reason for correction
// and resubmission.
// The "Applied to every invite" fields as the page first loads them — the single definition shared by data() and
// Reset, so a reset page is exactly a freshly loaded one.
function initialBatchFields() {
  return {
    // Subsection selections copied into every row (and, for Source App, also the application the batch grants).
    selectedApplication: null as InviteApplicationOption | null,
    selectedCountry: null as ICountry | null,
    selectedLanguage: (SupportedLanguages.find(l => l.code === DefaultLanguageCode) ?? null) as SupportedLanguageOption | null,
    // The Application Account Type submitted as AccountTypeName (validated against each Source App's config).
    selectedAccountType: null as InviteAccountTypeOption | null,
    // The per-user account type stamped into every row's AccountType cell (a different DataType vocabulary).
    selectedUserAccountType: null as InviteAccountTypeOption | null,

    // Tenant ApplicationAccount identity (required by the load endpoint, separate from the per-row CSV fields).
    name: '',
    externalId: '',
    authPathSegment: ''
  };
}

export default Vue.defineComponent({
  name: 'InviteBulkLoadPage',
  components: {
    CwePageHeader,
    InviteCsvUpload,
    InviteBulkGrid
  },
  data() {
    return {
      rows: [] as InviteRow[],
      // Remount key for the upload control: bumping it on Reset discards the chosen file and its column mapping.
      uploadKey: 0,
      // Per-cell errors reported by the server at parse time; cleared for a cell once the operator edits it.
      parseErrors: [] as InviteRowCellError[],
      // rowIndex -> server failure reason from the last submit, kept alongside the rows that failed to save.
      submitRowErrors: {} as Record<number, string>,
      // rowIndex -> "Created" / "Updated" for rows a submit saved. Those rows stay visible (read-only) with their
      // outcome in the Status column and are never resent.
      rowStatuses: {} as Record<number, InviteRowStatus>,
      // Last submit's tallies, or null before the first submit.
      submitSummary: null as { processed: number; created: number; updated: number; failed: number } | null,
      submitting: false,
      pageError: '' as string,

      // Reference data for the subsection pick-lists.
      applications: [] as InviteApplicationOption[],
      // Application (tenant) account types — children of "Applicaton Account Type"; drives AccountTypeName only.
      accountTypes: [] as InviteAccountTypeOption[],
      // Per-user account types — children of "ZBEdgeAccountType" (ClinicalUser/ZBEmployee); stamped into each row.
      userAccountTypes: [] as InviteAccountTypeOption[],
      countries: [] as ICountry[],
      languages: SupportedLanguages as SupportedLanguageOption[],

      // The "Applied to every invite" fields, at their starting values (Reset restores the same values).
      ...initialBatchFields()
    };
  },
  computed: {
    // "<rowIndex>|<columnKey>" -> message, consumed by the grid to highlight invalid cells. Server parse errors
    // (keyed by CSV header) seed the map; live client-side validation overlays it so edits highlight/clear instantly.
    cellErrors(): Record<string, string> {
      const map: Record<string, string> = {};
      for (const error of this.parseErrors) {
        const column = error.column ? InviteColumnByHeader[error.column] : undefined;
        if (!column)
          continue;
        const rowIndex = error.rowNumber - 1;
        map[`${rowIndex}|${column.key}`] = error.message ?? 'This value could not be read.';
      }
      const validation = validateInviteRows(this.rows, { skipRows: this.savedRowIndices });
      return { ...map, ...validation.cellErrors };
    },
    // Rows a submit already saved: read-only, excluded from validation, and never resent.
    savedRowIndices(): Set<number> {
      return new Set(Object.keys(this.rowStatuses).map(Number));
    },
    // Rows still to be submitted (everything not yet saved).
    unsavedRowCount(): number {
      return this.rows.length - this.savedRowIndices.size;
    },
    // The selected Source App's key, or null until one with a usable key is chosen.
    selectedApplicationKey(): string | null {
      const key = this.selectedApplication?.key;
      return key != null && key !== '' ? key : null;
    },
    // True once anything differs from a freshly loaded page — rows, an upload result, or any batch field.
    hasEnteredData(): boolean {
      const initial = initialBatchFields();
      return this.rows.length > 0
        || this.submitSummary != null
        || (Object.keys(initial) as (keyof typeof initial)[]).some(key => this[key] !== initial[key]);
    },
    // Submit is allowed only once every cell is valid AND the batch params the endpoint requires are present.
    canSubmit(): boolean {
      return !this.submitting
        && this.unsavedRowCount > 0
        && Object.keys(this.cellErrors).length === 0
        && this.selectedApplicationKey != null
        && this.selectedAccountType != null
        && this.selectedUserAccountType != null
        && this.selectedCountry != null
        && this.name.trim() !== ''
        && this.externalId.trim() !== '';
    },
    // A short explanation shown under a disabled submit button so the operator knows what is still blocking them.
    submitBlockedReason(): string {
      if (this.submitting || this.unsavedRowCount === 0 || this.canSubmit)
        return '';
      if (this.selectedApplicationKey == null)
        return 'Select a source app before importing.';
      if (this.selectedAccountType == null)
        return 'Select an application account type before importing.';
      if (this.selectedUserAccountType == null)
        return 'Select a user account type before importing.';
      if (this.selectedCountry == null)
        return 'Select a country before importing.';
      if (this.name.trim() === '' || this.externalId.trim() === '')
        return 'An account name and external ID are required before importing.';
      if (Object.keys(this.cellErrors).length > 0)
        return 'Resolve the highlighted cells before importing.';
      return '';
    }
  },
  watch: {
    // Re-copy the subsection values into every row whenever a selection changes (columns remain editable). Source App
    // feeds the sourceApp cell, User Account Type feeds the Account Type cell; Application Account Type has no per-row
    // column (it is submitted only as the batch AccountTypeName), so it is intentionally not watched here.
    selectedApplication() { this.applyBatchDefaultsToAllRows(); },
    selectedCountry() { this.applyBatchDefaultsToAllRows(); },
    selectedLanguage() { this.applyBatchDefaultsToAllRows(); },
    selectedUserAccountType() { this.applyBatchDefaultsToAllRows(); }
  },
  async created() {
    await this.loadReferenceData();
  },
  methods: {
    async loadReferenceData() {
      try {
        const [applications, accountTypes, userAccountTypes, countryResult] = await Promise.all([
          getInviteApplications(),
          getInviteAccountTypes(),
          getInviteUserAccountTypes(),
          getCountries()
        ]);
        this.applications = applications;
        this.accountTypes = accountTypes;
        this.userAccountTypes = userAccountTypes;
        this.countries = countryResult.countries ?? [];
        if (countryResult.countriesLoadFailed)
          this.pageError = 'The country list could not be loaded. Reload the page to try again.';
      } catch {
        this.pageError = 'The reference data could not be loaded. Reload the page to try again.';
      }
    },
    // Copies the current subsection selections into one row's cells. Only a made selection overwrites a cell.
    applyBatchDefaultsToRow(row: InviteRow) {
      if (this.selectedApplicationKey != null)
        row.sourceApp = this.selectedApplicationKey;
      if (this.selectedCountry)
        row.countryCode = this.selectedCountry.abbreviation;
      if (this.selectedLanguage)
        row.languageCode = this.selectedLanguage.code;
      // The per-row AccountType is the USER account type (ZBEdgeAccountType child), NOT the Application Account Type
      // submitted as AccountTypeName — the server validates each against a different DataType vocabulary.
      if (this.selectedUserAccountType)
        row.accountType = this.selectedUserAccountType.name ?? null;
    },
    applyBatchDefaultsToAllRows() {
      this.rows.forEach(row => this.applyBatchDefaultsToRow(row));
    },
    // A newly chosen file clears the previous file's batch error; a file that needs mapping reports nothing further
    // until its mapping is validated and parsed.
    onFileSelected() {
      this.pageError = '';
    },
    onParsed(result: ParseInvitesResult) {
      this.pageError = '';
      this.rows = result.rows;
      // Bring every phone into the grid phone component's shape (national number + bare dial code) so its editor
      // opens on the right country with the number formatted as the component formats it.
      this.rows.forEach(normalizeInvitePhone);
      this.parseErrors = result.rowErrors;
      this.submitRowErrors = {};
      this.rowStatuses = {};
      this.submitSummary = null;
      this.applyBatchDefaultsToAllRows();
    },
    onBatchError(messages: string[]) {
      this.pageError = messages.join(' ');
    },
    onCellChange(payload: { rowIndex: number; key: keyof InviteRow; value: string | boolean | null }) {
      const row = this.rows[payload.rowIndex];
      if (!row)
        return;
      (row as Record<string, unknown>)[payload.key as string] = payload.value;

      // The server's parse error for this cell no longer applies once it has been edited.
      this.parseErrors = this.parseErrors.filter(
        e => !(e.rowNumber - 1 === payload.rowIndex && e.column != null
          && InviteColumnByHeader[e.column]?.key === payload.key));

      // Editing a previously-failed row supersedes its submit reason.
      if (this.submitRowErrors[payload.rowIndex] != null) {
        const next = { ...this.submitRowErrors };
        delete next[payload.rowIndex];
        this.submitRowErrors = next;
      }
    },
    // Returns the page to how it first loads: every "Applied to every invite" field, the grid, every error and outcome,
    // and the chosen CSV with its mapping. Asks first when there is anything to lose.
    onReset() {
      if (this.hasEnteredData && !window.confirm('Clear all fields, invite rows and the uploaded CSV?'))
        return;

      // Rows go first so the batch-field watchers, which copy selections into rows, have nothing to touch.
      this.rows = [];
      Object.assign(this, initialBatchFields());
      this.parseErrors = [];
      this.submitRowErrors = {};
      this.rowStatuses = {};
      this.submitSummary = null;
      this.pageError = '';
      this.uploadKey++;
    },
    // Sends the grid's current rows to the export endpoint and saves the CSV it streams back.
    async onExport() {
      this.pageError = '';
      try {
        const { blob, fileName } = await exportInvitesCsv(this.rows);
        downloadBlob(blob, fileName);
      } catch (error) {
        if (error instanceof AdminInvitesBatchError)
          this.pageError = error.errors.map(e => e.localizedName).join(' ');
        else
          this.pageError = 'The invites could not be exported. Please try again.';
      }
    },
    onAddRow() {
      const row = createBlankInviteRow();
      this.applyBatchDefaultsToRow(row);
      this.rows.push(row);
    },
    onDeleteRow(rowIndex: number) {
      this.rows.splice(rowIndex, 1);
      // Drop errors for the removed row and shift down the row index of errors below it.
      this.parseErrors = this.parseErrors
        .filter(e => e.rowNumber - 1 !== rowIndex)
        .map(e => (e.rowNumber - 1 > rowIndex ? { ...e, rowNumber: e.rowNumber - 1 } : e));
      this.submitRowErrors = this.reindexAfterDelete(this.submitRowErrors, rowIndex);
      this.rowStatuses = this.reindexAfterDelete(this.rowStatuses, rowIndex);
    },
    // Rebuilds a rowIndex-keyed map after deleting a row: the deleted row's entry is dropped and every entry below it
    // shifts up by one to stay aligned with the spliced rows array.
    reindexAfterDelete<T>(map: Record<number, T>, deletedIndex: number): Record<number, T> {
      const next: Record<number, T> = {};
      for (const key of Object.keys(map)) {
        const index = Number(key);
        if (index < deletedIndex)
          next[index] = map[index];
        else if (index > deletedIndex)
          next[index - 1] = map[index];
      }
      return next;
    },
    async onSubmit() {
      if (!this.canSubmit)
        return;

      this.pageError = '';
      this.submitting = true;

      // Saved rows are never resent; everything else goes as-is (one invite per row).
      const rowsToSubmit = this.rows.filter((_row, index) => !this.savedRowIndices.has(index));
      const csv = serializeInvitesCsv(rowsToSubmit);
      const params: LoadInvitesRequest = {
        applicationKeys: this.selectedApplicationKey != null ? [this.selectedApplicationKey] : [],
        accountTypeName: this.selectedAccountType?.name ?? '',
        countryCode: this.selectedCountry?.abbreviation ?? null,
        name: this.name.trim() || null,
        externalId: this.externalId.trim() || null,
        authPathSegment: this.authPathSegment.trim() || null
      };

      try {
        const result = await loadInvites(csv, params);
        this.applyLoadResult(result, rowsToSubmit);
      } catch (error) {
        if (error instanceof AdminInvitesBatchError)
          this.pageError = error.errors.map(e => e.localizedName).join(' ');
        else
          this.pageError = 'The invites could not be submitted. Please try again.';
      } finally {
        this.submitting = false;
      }
    },
    // Submit reconciliation (req #7): the grid keeps every row. Rows saved by earlier submits stay first, unchanged;
    // then each just-submitted row in order — saved ones marked "Created"/"Updated" in the Status column (read-only),
    // failed ones carrying the API's reason (shown in red in the Status column) to fix and resubmit.
    applyLoadResult(result: LoadInvitesResult, submitted: InviteRow[]) {
      const outcomes = new Map(result.rows.map(r => [r.rowNumber, r]));

      const keptRows: InviteRow[] = [];
      const statuses: Record<number, InviteRowStatus> = {};
      const reasons: Record<number, string> = {};

      this.rows.forEach((row, index) => {
        if (!this.savedRowIndices.has(index))
          return;
        statuses[keptRows.length] = this.rowStatuses[index];
        keptRows.push(row);
      });

      submitted.forEach((row, index) => {
        const outcome = outcomes.get(index + 1);
        if (outcome?.succeeded)
          statuses[keptRows.length] = outcome.created ? 'Created' : 'Updated';
        else
          reasons[keptRows.length] = outcome?.error ?? 'This row could not be saved.';
        keptRows.push(row);
      });

      const saved = result.rows.filter(r => r.succeeded);
      this.rows = keptRows;
      this.rowStatuses = statuses;
      this.submitRowErrors = reasons;
      this.parseErrors = [];
      this.submitSummary = {
        processed: result.processed,
        created: saved.filter(r => r.created).length,
        updated: saved.filter(r => !r.created).length,
        failed: result.failed
      };
    }
  }
});
</script>

<style lang="scss">
// Styled after /dashboard: the diamonds background behind a centered, max-width column, with each content block on a
// white rounded panel (as the dashboard's app cards and "more apps" band sit on white over the background).
.inviteBulkLoad {
  background-image: url('/public/img/zbedge-diamonds-background.png');
  background-size: cover;
  background-position: center;
  display: flex;
  flex: 2;
  flex-direction: column;
  justify-content: flex-start;

  #MainInviteBulkLoad {
    width: $maxInnerPageWidth;
    margin: auto;
    display: flex;
    flex: 2;
    flex-direction: column;
    padding-inline: 0;
    padding-bottom: $standard_space;
  }

  .sectionHeader {
    padding-top: $standard_space;
    padding-bottom: 4px;
    margin-bottom: 1rem;
    align-self: flex-start;
    border-bottom: 1px solid $medium_light_gray;
    width: 100%;
  }

  .intro {
    margin-bottom: $standard_space;
  }

  .pagePanel {
    background: $white;
    border: 2px solid $white;
    border-radius: $element_border_radius;
    padding: $standard_space;
    margin-bottom: $standard_space;
  }

  .subHeader {
    margin-top: 0;
    margin-bottom: 6px;
  }

  .batchParams {
    .batchHint {
      color: $faint_text;
      margin-bottom: 10px;
    }

    .paramGrid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 10px;
    }
  }

  // "Invites" heading and its actions (export, add) share one row, vertically centered, the actions pushed right.
  .invitesHeader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 10px;

    .invitesHeading {
      margin: 0;
    }

    .invitesActions {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    // One width step below the standard 200px button minimum (150px, the size buttons drop to on small screens).
    .addInviteBtn {
      min-width: 150px;
      cursor: pointer;
    }

    // Icon-only: a round outline button sized to the standard button height.
    .exportBtn {
      min-width: 0;
      width: 40px;
      height: 40px;
      padding: 0;
      border-radius: 50%;
      background: $white;
      color: $primary_color;
      border-color: $primary_color;

      &:hover {
        background: $primary_color;
        color: $white;
      }
    }
  }

  .infoBox {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 14px;
    margin-bottom: $standard_space;
    background: rgba($primary_color, 0.08);
    border: 1px solid rgba($primary_color, 0.35);
    border-left: 4px solid $primary_color;
    border-radius: $element_border_radius;
    color: $primary_text;

    .infoIcon {
      color: $primary_color;
      margin-top: 3px;
    }
  }

  .submitSection {
    margin-top: $standard_space;

    .submitActions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: $standard_space;
    }

    .submitHint {
      margin: 0;
    }

    // Two width steps below the standard 200px button minimum (200 -> 150 -> 120px).
    .resetBtn,
    .importBtn {
      min-width: 120px;
    }

    // Secondary action: an outline in the secondary button color, so it reads as available (the filled gray
    // .button-secondary looks disabled beside the primary button). Pinned left; the hint and Import stay right.
    .resetBtn {
      margin-right: auto;
      background: $white;
      color: $dark_accent_color;
      border: 2px solid $secondary_button_color;

      &:hover {
        background: $secondary_button_color;
        color: $white;
        border-color: $secondary_button_color;
      }

      &:disabled {
        background: $white;
        color: $secondary_button_color_disabled;
        border-color: $secondary_button_color_disabled;
      }
    }
  }

  @media screen and (max-width: 1250px) {
    #MainInviteBulkLoad {
      padding-inline: 2%;
      width: 100%;
      max-width: unset;
    }
  }
}
</style>
